import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useFeaturedToggle } from "@/hooks/superadmin/useFeaturedToggle";
import { useProfileDelete } from "@/hooks/superadmin/useProfileDelete";
import PaginationControls from "@/components/designer-grid/PaginationControls";
import { ContentPagination } from "@/v2/services/types/serviceInterfaces";
import {
  Shield,
  Search,
  UserCog,
  RefreshCcw,
  AlertTriangle,
  Star,
  Trash2,
  Pencil,
  MoreVertical,
  Mail,
  Image,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage } from "@radix-ui/react-avatar";
import { useUpdateUserEmail } from "@/hooks/superadmin/useUpdateUserEmail";
import { usePortfolioConversion } from "@/hooks/superadmin/usePortfolioConversion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getProfileUrl } from "@/utils/profileRedirects";
import { ALL_COUNTRIES } from '@/data/countries';
import InviteUserDialog from '@/components/superadmin/InviteUserDialog';

// Define the user role type to match the database enum
type UserRole = "designer" | "admin" | "superadmin";

interface User {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  username: string | null;
  location: string | null;
  is_admin: boolean;
  is_superadmin: boolean | null;
  display_name: string | null;
  is_featured: boolean | null;
  avatar_url: string | null;
  roles: string[];
  profession?: string | null;
  username_slug?: string | null; // Added for getProfileUrl
}

// Utility function to map location id or label to display label
function getCountryLabel(location: string | null | undefined): string {
  if (!location) return '-';
  // Replace dashes with spaces for normalization
  const normalizedLocation = location.replace(/-/g, ' ');
  // Try to find by id (case-insensitive)
  const matchById = ALL_COUNTRIES.find(
    (c) => c.id.toLowerCase() === normalizedLocation.toLowerCase()
  );
  if (matchById) return matchById.label;
  // Try to find by label (case-insensitive)
  const matchByLabel = ALL_COUNTRIES.find(
    (c) => c.label.toLowerCase() === normalizedLocation.toLowerCase()
  );
  if (matchByLabel) return matchByLabel.label;
  // Fallback: capitalize each word properly
  return normalizedLocation
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

const UserManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isPromoting, setIsPromoting] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole>("designer");
  const [actionReason, setActionReason] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState<
    "promote" | "demote" | "resetPassword"
  >("promote");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10); // Fixed page size
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false); // New filter state
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { toggleFeatured, isLoading: isFeaturedLoading } = useFeaturedToggle({
    onSuccess: (data) => {
      console.log("Featured status updated:", data);
      // Invalidate and refetch the users query to update the UI
      queryClient.invalidateQueries({
        queryKey: ["superadmin-users"],
      });
    },
    onError: (error) => {
      console.error("Failed to update featured status:", error);
      // Invalidate queries to revert any optimistic updates
      queryClient.invalidateQueries({
        queryKey: ["superadmin-users"],
      });
    },
  });
  const { deleteProfile, isLoading: isDeleteLoading } = useProfileDelete();
  const [profileToDelete, setProfileToDelete] = useState<string | null>(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const { updateUserEmail, isLoading: isEmailUpdateLoading } = useUpdateUserEmail({
    onSuccess: () => {
      setShowEmailDialog(false);
      setNewEmail("");
      refetch();
    }
  });

  // Portfolio conversion state and hook
  const [showPortfolioDialog, setShowPortfolioDialog] = useState(false);
  const [portfolioUser, setPortfolioUser] = useState<User | null>(null);
  const { 
    convertUserPortfolio, 
    isConverting, 
    progress, 
    totalItems, 
    convertedItems, 
    totalImages, 
    convertedImages, 
    failedConversions, 
    errors,
    reset: resetPortfolioConversion 
  } = usePortfolioConversion();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when search changes
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when featured filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [showFeaturedOnly]);

  // Fetch users with pagination
  const {
    data: usersData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      "superadmin-users",
      currentPage,
      pageSize,
      debouncedSearchTerm,
      showFeaturedOnly,
    ],
    queryFn: async () => {
      const offset = (currentPage - 1) * pageSize;

      // Build the count query
      let countQuery = supabase
        .from("profiles")
        .select("*", { count: "exact", head: true });

      // Apply featured filter
      if (showFeaturedOnly) {
        countQuery = countQuery.eq("is_featured", true);
      }

      // Apply search filter if debouncedSearchTerm exists
      if (debouncedSearchTerm.trim()) {
        countQuery = countQuery.or(
          `email.ilike.%${debouncedSearchTerm}%,display_name.ilike.%${debouncedSearchTerm}%,username.ilike.%${debouncedSearchTerm}%,location.ilike.%${debouncedSearchTerm}%`
        );
      }

      // Get total count
      const { count, error: countError } = await countQuery;

      if (countError) throw new Error(countError.message);

      // Get paginated data
      let dataQuery = supabase
        .from("profiles")
        .select(
          "id, username, location, is_admin, is_superadmin, email, created_at, display_name, is_featured, avatar_url, profession, username_slug"
        )
        .range(offset, offset + pageSize - 1)
        .order("created_at", { ascending: false });

      // Apply featured filter
      if (showFeaturedOnly) {
        dataQuery = dataQuery.eq("is_featured", true);
      }

      // Apply the same search filter to data query
      if (debouncedSearchTerm.trim()) {
        dataQuery = dataQuery.or(
          `email.ilike.%${debouncedSearchTerm}%,display_name.ilike.%${debouncedSearchTerm}%,username.ilike.%${debouncedSearchTerm}%,location.ilike.%${debouncedSearchTerm}%`
        );
      }

      const { data: profiles, error: profilesError } = await dataQuery;

      if (profilesError) throw new Error(profilesError.message);

      // Transform the data to match the User interface
      const transformedUsers: User[] = (profiles || []).map((profile) => ({
        id: profile.id,
        email: profile.email,
        created_at: profile.created_at,
        last_sign_in_at: null, // This field isn't available in profiles table
        username: profile.username,
        location: profile.location,
        is_admin: profile.is_admin,
        is_superadmin: profile.is_superadmin,
        display_name: profile.display_name,
        is_featured: profile.is_featured,
        avatar_url: profile.avatar_url,
        roles: [], // This would need to be fetched separately if needed
        profession: profile.profession,
        username_slug: profile.username_slug, // Add username_slug
      }));

      // Calculate pagination info
      const totalCount = count || 0;
      const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
      const hasNextPage = currentPage < totalPages;
      const hasPrevPage = currentPage > 1;

      const pagination: ContentPagination = {
        currentPage,
        totalPages,
        hasNextPage,
        hasPrevPage,
        totalCount,
      };

      console.log("Users data:", { users: transformedUsers, pagination });
      return { users: transformedUsers, pagination };
    },
  });

  const users = usersData?.users || [];
  const pagination = usersData?.pagination || {
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
    totalCount: 0,
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Filter users based on search term - this is now handled in the query
  const filteredUsers = users;

  // Handle featured toggle with optimistic updates
  const handleFeaturedToggle = (user: User) => {
    const currentQueryKey = [
      "superadmin-users",
      currentPage,
      pageSize,
      debouncedSearchTerm,
      showFeaturedOnly,
    ];

    // Optimistically update the UI
    queryClient.setQueryData(
      currentQueryKey,
      (
        oldData: { users: User[]; pagination: ContentPagination } | undefined
      ) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          users: oldData.users.map((u: User) =>
            u.id === user.id ? { ...u, is_featured: !u.is_featured } : u
          ),
        };
      }
    );

    // Perform the actual mutation
    toggleFeatured(user.id, user.is_featured || false);
  };

  // Handle role change
  const handleRoleAction = async () => {
    if (!selectedUser) return;

    setIsPromoting(true);
    try {
      let success = false;
      const userId = selectedUser.id;

      if (dialogAction === "promote") {
        const { error } = await supabase.from("user_roles").insert({
          user_id: userId,
          role: selectedRole,
          created_by: (await supabase.auth.getUser()).data.user?.id,
        });

        if (error) throw error;

        await supabase.from("audit_logs").insert({
          actor_id: (await supabase.auth.getUser()).data.user?.id,
          action_type: "PROMOTE_USER",
          target_table: "user_roles",
          target_id: userId,
          details: { role: selectedRole },
          reason: actionReason || "Role promotion",
        });

        success = true;
      } else if (dialogAction === "demote") {
        const { error } = await supabase
          .from("user_roles")
          .delete()
          .eq("user_id", userId)
          .eq("role", selectedRole);

        if (error) throw error;

        await supabase.from("audit_logs").insert({
          actor_id: (await supabase.auth.getUser()).data.user?.id,
          action_type: "DEMOTE_USER",
          target_table: "user_roles",
          target_id: userId,
          details: { role: selectedRole },
          reason: actionReason || "Role demotion",
        });

        success = true;
      } else if (dialogAction === "resetPassword") {
        const { error } = await supabase.auth.resetPasswordForEmail(
          selectedUser.email,
          { redirectTo: `${window.location.origin}/auth?view=update-password` }
        );

        if (error) throw error;

        await supabase.from("audit_logs").insert({
          actor_id: (await supabase.auth.getUser()).data.user?.id,
          action_type: "RESET_PASSWORD",
          target_table: "auth.users",
          target_id: userId,
          reason: actionReason || "Password reset",
        });

        success = true;
      }

      if (success) {
        toast({
          title: "Success",
          description: `User ${
            dialogAction === "promote"
              ? "promoted to"
              : dialogAction === "demote"
              ? "demoted from"
              : "password reset sent for"
          } ${dialogAction === "resetPassword" ? "" : selectedRole}`,
        });

        refetch();
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      toast({
        title: "Error",
        description: `Failed to ${dialogAction}: ${errorMessage}`,
        variant: "destructive",
      });
    } finally {
      setIsPromoting(false);
      setShowConfirmDialog(false);
      setActionReason("");
    }
  };

  const openPromoteDialog = (user: User, role: UserRole) => {
    setSelectedUser(user);
    setSelectedRole(role);
    setDialogAction("promote");
    setShowConfirmDialog(true);
  };

  const openDemoteDialog = (user: User, role: UserRole) => {
    setSelectedUser(user);
    setSelectedRole(role);
    setDialogAction("demote");
    setShowConfirmDialog(true);
  };

  const openResetPasswordDialog = (user: User) => {
    setSelectedUser(user);
    setDialogAction("resetPassword");
    setShowConfirmDialog(true);
  };

  const handleEmailUpdate = () => {
    if (!selectedUser || !newEmail) return;
    updateUserEmail(selectedUser.id, newEmail);
  };

  const handlePortfolioConversion = async () => {
    console.log('handlePortfolioConversion called. portfolioUser:', portfolioUser);
    if (!portfolioUser) return;
    try {
      console.log('Calling convertUserPortfolio with userId:', portfolioUser.id);
      await convertUserPortfolio(portfolioUser.id);
      console.log('convertUserPortfolio finished');
      setShowPortfolioDialog(false);
      setPortfolioUser(null);
      resetPortfolioConversion();
    } catch (error) {
      console.error('Portfolio conversion failed:', error);
    }
  };

  const openPortfolioDialog = (user: User) => {
    console.log('openPortfolioDialog called. user:', user);
    setPortfolioUser(user);
    setShowPortfolioDialog(true);
    resetPortfolioConversion();
  };

  return (
    <div className="space-y-6">
      
      <div>
        <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="relative w-full max-w-sm">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by email, name or location..."
                  className="pl-8 w-full"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Featured Filter Toggle */}
              <div className="flex items-center gap-2">
                <Switch
                  checked={showFeaturedOnly}
                  onCheckedChange={setShowFeaturedOnly}
                  className="data-[state=checked]:bg-yellow-500"
                />
                <span className="text-sm font-medium whitespace-nowrap">
                  Featured Only
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {pagination.totalCount}{" "}
                {showFeaturedOnly ? "featured" : "total"} users
              </span>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCcw className="mr-2 h-4 w-4" />
                Refresh
              </Button>

              <InviteUserDialog onInviteSent={() => refetch()} />
            </div>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Discipline</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Featured</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      Loading users...
                    </TableCell>
                  </TableRow>
                ) : filteredUsers && filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Avatar className="w-8 h-8 rounded-full">
                            <AvatarImage
                              className="rounded-full object-cover w-8 h-8"
                              src={
                                user.avatar_url ||
                                "/public/lovable-uploads/06a7565c-628b-47f8-84c3-657eb3f0f9d6.png"
                              }
                            />
                          </Avatar>
                          <div className="flex flex-col">
                            <a
                              href={getProfileUrl(user.id, user.username_slug)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline font-semibold"
                            >
                              {user.display_name || user.username || "Unnamed User"}
                            </a>
                            <span className="text-xs text-muted-foreground">
                              {user.email}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{user.profession || "-"}</TableCell>
                      <TableCell>{getCountryLabel(user?.location)}</TableCell>
                      <TableCell>
                        {user.created_at
                          ? new Date(user.created_at).toLocaleString()
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={user.is_featured || false}
                            onCheckedChange={() => handleFeaturedToggle(user)}
                            disabled={isFeaturedLoading(user.id)}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                              <span className="sr-only">Open menu</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedUser(user);
                                setNewEmail(user.email);
                                setShowEmailDialog(true);
                              }}
                            >
                              <Mail className="mr-2 h-4 w-4" />
                              <span>Update Email</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => openPortfolioDialog(user)}
                            >
                              <Image className="mr-2 h-4 w-4" />
                              <span>Convert Portfolio Images</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setProfileToDelete(user.id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              <span>Delete Profile</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      {showFeaturedOnly ? (
                        <div className="flex flex-col items-center gap-2">
                          <Star className="h-8 w-8 text-muted-foreground" />
                          <span>No featured users found</span>
                          {debouncedSearchTerm.trim() && (
                            <span className="text-sm text-muted-foreground">
                              Try adjusting your search or disable the featured
                              filter
                            </span>
                          )}
                        </div>
                      ) : (
                        "No users found"
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
      </div>

      {/* Add Pagination Controls */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center">
          <PaginationControls
            pagination={pagination}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialogAction === "promote"
                ? "Promote User"
                : dialogAction === "demote"
                ? "Demote User"
                : "Reset Password"}
            </DialogTitle>
            <DialogDescription>
              {dialogAction === "promote" && (
                <>
                  Promote {selectedUser?.username || selectedUser?.email} to{" "}
                  {selectedRole}
                </>
              )}
              {dialogAction === "demote" && (
                <>
                  Remove {selectedRole} role from{" "}
                  {selectedUser?.username || selectedUser?.email}
                </>
              )}
              {dialogAction === "resetPassword" && (
                <>Send password reset email to {selectedUser?.email}</>
              )}
            </DialogDescription>
          </DialogHeader>

          {dialogAction === "promote" && selectedRole === "superadmin" && (
            <div className="bg-amber-50 p-3 rounded-md flex items-start gap-2 mb-4">
              <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
              <div className="text-sm text-amber-800">
                <strong>Warning:</strong> Superadmins have full access to all
                platform functions and data. Only promote trusted individuals to
                this role.
              </div>
            </div>
          )}

          <div className="grid gap-4">
            {dialogAction === "promote" && (
              <div>
                <label className="text-sm font-medium block mb-1">Role</label>
                <Select
                  value={selectedRole}
                  onValueChange={(value: string) =>
                    setSelectedRole(value as UserRole)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="designer">Designer</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="superadmin">Superadmin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <label className="text-sm font-medium block mb-1">
                Reason (Optional)
              </label>
              <Textarea
                placeholder="Enter reason for this action..."
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                This will be logged in the audit trail for future reference
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRoleAction}
              disabled={isPromoting}
              variant={
                dialogAction === "demote" ||
                (dialogAction === "promote" && selectedRole === "superadmin")
                  ? "destructive"
                  : "default"
              }
            >
              {isPromoting ? "Processing..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Update Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="sm:max-w-[425px] bg-white rounded-lg shadow-lg fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-8">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Update User Email</DialogTitle>
            <DialogDescription className="text-base">
              Update email for {selectedUser?.username || selectedUser?.email}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                New Email
              </label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter new email address"
                className="h-10"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowEmailDialog(false)}
              className="h-10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEmailUpdate}
              disabled={isEmailUpdateLoading || !newEmail}
              className="h-10"
            >
              {isEmailUpdateLoading ? "Updating..." : "Update Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!profileToDelete} onOpenChange={() => setProfileToDelete(null)}>
        <DialogContent className="sm:max-w-[425px] bg-white rounded-lg shadow-lg fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-8">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Delete Profile</DialogTitle>
            <DialogDescription className="text-base">
              Are you sure you want to delete this profile? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setProfileToDelete(null)}
              className="h-10"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (profileToDelete) {
                  deleteProfile(profileToDelete);
                  setProfileToDelete(null);
                }
              }}
              className="h-10"
            >
              Delete
            </Button>
          </DialogFooter>
                  </DialogContent>
        </Dialog>

        {/* Portfolio Conversion Dialog */}
        <Dialog open={showPortfolioDialog} onOpenChange={setShowPortfolioDialog}>
          <DialogContent className="sm:max-w-[500px] bg-white rounded-xl p-8 shadow-lg">
            <DialogHeader>
              <DialogTitle>Convert Portfolio Images</DialogTitle>
              <DialogDescription>
                Convert base64 portfolio images to proper storage URLs for{" "}
                {portfolioUser?.display_name || portfolioUser?.username || portfolioUser?.email}
              </DialogDescription>
            </DialogHeader>

            {isConverting ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <span className="text-sm text-muted-foreground min-w-[60px]">
                    {progress}%
                  </span>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div>Processing items: {convertedItems}/{totalItems}</div>
                  <div>Converting images: {convertedImages}/{totalImages}</div>
                  {failedConversions > 0 && (
                    <div className="text-red-600">Failed conversions: {failedConversions}</div>
                  )}
                </div>

                {errors.length > 0 && (
                  <div className="bg-red-50 p-3 rounded-md">
                    <div className="text-sm font-medium text-red-800 mb-2">Errors:</div>
                    <div className="text-xs text-red-700 space-y-1 max-h-32 overflow-y-auto">
                      {errors.map((error, index) => (
                        <div key={index}>• {error}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-md">
                  <div className="flex items-start gap-3">
                    <Image className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <div className="font-medium mb-1">What this does:</div>
                      <ul className="space-y-1 text-xs">
                        <li>• Scans the user's portfolio for base64 encoded images</li>
                        <li>• Converts them to proper storage URLs</li>
                        <li>• Updates the user's portfolio in the database</li>
                        <li>• Improves performance and reduces data usage</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowPortfolioDialog(false);
                  setPortfolioUser(null);
                  resetPortfolioConversion();
                }}
                disabled={isConverting}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePortfolioConversion}
                disabled={isConverting || !portfolioUser}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isConverting ? "Converting..." : "Convert Images"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

export default UserManagement;
